/**
 * PageMeta — thin wrapper over react-helmet-async for per-route
 * <title> / <meta> overrides. Keeps auth pages SEO-visible even though
 * the app shell defaults to `noindex, nofollow`.
 *
 * Usage:
 *   <PageMeta
 *     title="Sign in"
 *     description="Sign in to AnooreHR."
 *     indexable
 *     canonical="https://app.anoorehr.com/login"
 *   />
 *
 * Title is composed as `{title} · AnooreHR` unless `absoluteTitle` is set.
 */

import { Helmet } from 'react-helmet-async';

export interface PageMetaProps {
  title: string;
  description?: string;
  canonical?: string;
  /** Set true to override the default `noindex, nofollow` from index.html. */
  indexable?: boolean;
  /** If true, use `title` as the full <title> — skip the ` · AnooreHR` suffix. */
  absoluteTitle?: boolean;
  /** Optional og:image override. Defaults to the shared AnooreHR og image. */
  ogImage?: string;
}

const DEFAULT_OG_IMAGE = 'https://anoorehr.com/og-image.png';

export default function PageMeta({
  title,
  description,
  canonical,
  indexable = false,
  absoluteTitle = false,
  ogImage = DEFAULT_OG_IMAGE,
}: PageMetaProps) {
  const fullTitle = absoluteTitle ? title : `${title} · AnooreHR`;
  const robots = indexable ? 'index, follow' : 'noindex, nofollow';

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="robots" content={robots} />
      {description && <meta name="description" content={description} />}
      {canonical && <link rel="canonical" href={canonical} />}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}
